import { WebMercatorViewport } from '@deck.gl/core/typed';
import bbox from '@turf/bbox';
import booleanPointInPolygon from '@turf/boolean-point-in-polygon';
import { Geometry } from 'geojson';

import {
  Bounds,
  BoundsInfo,
  FitGeoLocationsReturn,
  GeoLocation,
  Viewport,
  ViewportInitialized,
  ViewportState,
} from '@hcs/types';
import { isBounds } from '@hcs/utils';

const METERS_IN_ONE_LATITUDE_DEGREE = 111111;

export const isViewportInitialized = (
  viewport: Viewport,
): viewport is ViewportInitialized => {
  if (
    viewport?.width &&
    viewport?.height &&
    viewport?.latitude &&
    viewport?.longitude &&
    viewport?.zoom
  ) {
    return true;
  }
  return false;
};

export interface ViewportSearchParamNames {
  latitudeParamName: string;
  longitudeParamName: string;
  zoomParamName: string;
}

export const getViewportFromSearchParams = ({
  searchParams,
  searchParamNames,
}: {
  searchParams: URLSearchParams;
  searchParamNames: ViewportSearchParamNames;
}): Pick<Viewport, 'latitude' | 'longitude' | 'zoom'> | null => {
  const latitude = searchParams.get(searchParamNames.latitudeParamName);
  const longitude = searchParams.get(searchParamNames.longitudeParamName);
  const zoom = searchParams.get(searchParamNames.zoomParamName);
  if (latitude !== null && longitude !== null && zoom !== null) {
    // attempt to parse
    const parsedLatitude = parseFloat(latitude);
    const parsedLongitude = parseFloat(longitude);
    const parsedZoom = parseFloat(zoom);
    if (isNaN(parsedLatitude) || isNaN(parsedLongitude) || isNaN(parsedZoom)) {
      return null;
    } else {
      return {
        latitude: parsedLatitude,
        longitude: parsedLongitude,
        zoom: parsedZoom,
      };
    }
  }
  return null;
};

/**
 * Calculate bounding box from an array of GeoLocation coordinates
 */
const getBoundsFromCoords = (
  coords: GeoLocation[],
): [[number, number], [number, number]] => {
  const lats: number[] = [];
  const lngs: number[] = [];
  coords.forEach((geoLocation) => {
    if (geoLocation.latitude) {
      lats.push(geoLocation.latitude);
    }
    if (geoLocation.longitude) {
      lngs.push(geoLocation.longitude);
    }
  });
  const maxLat = Math.max(...lats);
  const minLat = Math.min(...lats);
  const maxLng = Math.max(...lngs);
  const minLng = Math.min(...lngs);
  return [
    [minLng, minLat],
    [maxLng, maxLat],
  ];
};

export const fitGeoLocationsToViewPort = (
  coords: GeoLocation[],
  mapState: { viewport: Omit<ViewportState, 'latitude' | 'longitude'> },
  padding: number,
): FitGeoLocationsReturn | undefined => {
  let newBounds = getBoundsFromCoords(coords);
  if (coords.length === 1) {
    newBounds = padBoundsByMeters(newBounds, 500); // pad by 500 meters if only a single point in coords
  }
  try {
    const nextViewport = new WebMercatorViewport({
      ...mapState?.viewport,
    }).fitBounds(newBounds, {
      padding,
    });
    // Get centre coordinates
    const coords = nextViewport.unproject([
      nextViewport.width / 2,
      nextViewport.height / 2,
    ]);
    return {
      viewport: nextViewport,
      // assert the array items as truthy since we know the values will always exist
      coords: { lng: coords[0]!, lat: coords[1]! },
    };
  } catch {
    // This is expected under fairly common conditions, no need to log
    console.warn('Unable fit markers in map viewport');
  }
  return undefined;
};

/**
 * Fits a GeoJSON Geometry to a bounding box for use in passing to HcMap `fitBounds` prop
 */
export const fitGeometryToBbox = (geometry: Geometry): GeoLocation[] | null => {
  try {
    const [west, south, east, north] = bbox(geometry);

    return [
      { latitude: south, longitude: west },
      { latitude: north, longitude: east },
    ];
  } catch (error) {
    // This is expected under fairly common conditions, no need to log
    console.warn('Unable to fit geometry to bbox', error);
  }
  return null;
};

export const setViewportSearchParams = ({
  searchParams,
  viewport,
  searchParamNames,
}: {
  searchParams: URLSearchParams;
  viewport: Viewport;
  searchParamNames: ViewportSearchParamNames;
}): URLSearchParams => {
  if (viewport) {
    searchParams.set(
      searchParamNames.latitudeParamName,
      viewport.latitude.toString(),
    );
    searchParams.set(
      searchParamNames.longitudeParamName,
      viewport.longitude.toString(),
    );
    searchParams.set(searchParamNames.zoomParamName, viewport.zoom.toString());
  }
  return searchParams;
};

export const getBoundsInfoFromViewportState = (
  viewportState: Viewport,
): BoundsInfo | undefined => {
  const viewport = new WebMercatorViewport(viewportState);
  const southWest = viewport.unproject([0, viewport.height]);
  const northEast = viewport.unproject([viewport.width, 0]);
  const bounds = [southWest, northEast];

  if (isBounds(bounds)) {
    return {
      // assert the array items as truthy since we know the values will always exist
      southWest: { lng: southWest[0]!, lat: southWest[1]! },
      northEast: { lng: northEast[0]!, lat: northEast[1]! },
      bounds,
    };
  }
  return undefined;
};

/**
 * Given bounds and a point, return whether the point lies within the bounds
 */
export const getIsPointInsideBounds = (
  bounds: Bounds,
  coord: [number, number],
): boolean => {
  const newBounds = getBoundsFromCoords([
    {
      longitude: bounds[0][0],
      latitude: bounds[0][1],
    },
    {
      longitude: bounds[1][0],
      latitude: bounds[1][1],
    },
  ]);
  // Form a polygon from the bounds
  const polygon = {
    type: 'Feature' as const,
    geometry: {
      type: 'Polygon' as const,
      coordinates: [
        [
          [newBounds[0][0], newBounds[0][1]],
          [newBounds[1][0], newBounds[0][1]],
          [newBounds[1][0], newBounds[1][1]],
          [newBounds[0][0], newBounds[1][1]],
          [newBounds[0][0], newBounds[0][1]],
        ],
      ],
    },
    properties: {},
  };
  return booleanPointInPolygon(coord, polygon);
};

/**
 * Offset the given point by certain number of meters in each direction
 */
export const offsetLatLngPointByMeters = (
  { latitude, longitude }: { latitude: number; longitude: number },
  meters: [number, number],
): { latitude: number; longitude: number } => {
  const metersX = meters?.[0] || 0;
  const metersY = meters?.[1] || 0;

  return {
    latitude: latitude + metersY / METERS_IN_ONE_LATITUDE_DEGREE,
    longitude:
      longitude +
      metersX /
        (METERS_IN_ONE_LATITUDE_DEGREE * Math.cos((latitude * Math.PI) / 180)),
  };
};

/**
 * Pad the given bounds by a certain number of meters in all directions
 */
export const padBoundsByMeters = (bounds: Bounds, meters: number): Bounds => {
  const paddedSW = offsetLatLngPointByMeters(
    { latitude: bounds[0][1], longitude: bounds[0][0] },
    [-meters, -meters],
  );
  const paddedNE = offsetLatLngPointByMeters(
    { latitude: bounds[1][1], longitude: bounds[1][0] },
    [meters, meters],
  );
  return [
    [paddedSW.longitude, paddedSW.latitude],
    [paddedNE.longitude, paddedNE.latitude],
  ];
};
